import { addHours } from "date-fns";
import { useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";

import {
  LearnerDetailsModalStateProviderProps,
  LearnerFormFields,
} from "./types";
import { getInitialFormState } from "../../../components/Forms/LearnerDetailsForm/helpers";

import { Salutation } from "@hubcapp/graphql";
import { learnerDetailsFormFieldsValidationSchema } from "./validation/learnerDetailsFormFieldsSchema";
import LearnerDetailsModal from "./LearnerDetailsModal";
import useMutationUpdateLearner from "./hooks/useMutationUpdateLearner";
import useQueryLearnerDetailsModal from "./hooks/useQueryLearnerDetailsModal";

const LearnerDetailsModalStateProvider: React.FC<LearnerDetailsModalStateProviderProps> = ({
  learnerId,
  isOpen = false,
  onToggleLearnerDetailsModal,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const { data, loading, refetch } = useQueryLearnerDetailsModal(learnerId);

  const {
    errors,
    formState: { isValid, dirty },
    register,
    setValue,
    triggerValidation,
    watch,
    handleSubmit: submitWrapper,
  } = useForm<LearnerFormFields>({
    defaultValues: data?.learner
      ? getInitialFormState(data?.learner)
      : undefined,
    mode: "onBlur",
    reValidateMode: "onChange",
    validationSchema: learnerDetailsFormFieldsValidationSchema,
    validateCriteriaMode: "firstError",
  });

  useEffect(() => {
    register({ name: "title" });
    register({ name: "firstName" });
    register({ name: "lastName" });
    register({ name: "contactMobile" });
    register({ name: "contactEmail" });
    register({ name: "addressLine1" });
    register({ name: "addressLine2" });
    register({ name: "addressTown" });
    register({ name: "addressPostcode" });
    register({ name: "dateOfBirth" });
  }, []);

  useEffect(() => {
    setValue([
      {
        title: data?.learner.name.title || Salutation.Mx,
      },
      {
        firstName: data?.learner.name.firstName,
      },
      {
        lastName: data?.learner.name.lastName,
      },
      {
        contactMobile: data?.learner.mobileNumber,
      },
      {
        contactEmail: data?.learner.emailAddress,
      },
      {
        addressLine1: data?.learner.pickupAddress?.line1 || "",
      },
      {
        addressLine2: data?.learner.pickupAddress?.line2 || "",
      },
      {
        addressTown: data?.learner.pickupAddress?.town || "",
      },
      {
        addressPostcode: data?.learner.pickupAddress?.postcode,
      },
      {
        dateOfBirth: data?.learner.dateOfBirth,
      },
    ]);
  }, [data?.learner]);

  const values = watch([
    "title",
    "firstName",
    "lastName",
    "contactMobile",
    "contactEmail",
    "addressLine1",
    "addressLine2",
    "addressTown",
    "addressPostcode",
    "dateOfBirth",
  ]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("title", Salutation[e.target.value as Salutation]);
    triggerValidation("title");
  };

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("firstName", e.target.value);
    triggerValidation("firstName");
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("lastName", e.target.value);
    triggerValidation("lastName");
  };

  const handleContactMobileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setValue("contactMobile", e.target.value);
    triggerValidation("contactMobile");
  };

  const handleContactEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("contactEmail", e.target.value);
    triggerValidation("contactEmail");
  };

  const handleAddressLine1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("addressLine1", e.target.value);
    triggerValidation("addressLine1");
  };

  const handleAddressLine2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("addressLine2", e.target.value);
    triggerValidation("addressLine2");
  };

  const handleAddressTownChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("addressTown", e.target.value);
    triggerValidation("addressTown");
  };

  const handleAddressPostcodeChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setValue("addressPostcode", e.target.value);
    triggerValidation("addressPostcode");
  };

  const handleDateOfBirthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value !== null) {
      setValue("dateOfBirth", new Date(e.target.value));
      triggerValidation("dateOfBirth");
    } else {
      setValue("dateOfBirth", new Date("00/00/0000"));
      triggerValidation("dateOfBirth");
    }
  };

  const [
    mutationLearnerDetails,
    { loading: saving },
  ] = useMutationUpdateLearner();

  const onSaveClicked = submitWrapper(values => {
    setSubmitting(true);

    mutationLearnerDetails({
      variables: {
        input: {
          id: learnerId,
          ...values,
          dateOfBirth: addHours(new Date(values.dateOfBirth), 12).toISOString(),
          pickupAddressUpdate: true,
          billingAddressUpdate: false,
        },
      },
      refetchQueries: ["LearnerName"],
    })
      .then(() => refetch())
      .then(() => onToggleLearnerDetailsModal())
      .then(() => setSubmitting(false));
  });

  const onCancelClicked = () => {
    onToggleLearnerDetailsModal();
  };

  if (!data?.learner || loading) return null;

  return (
    <LearnerDetailsModal
      loading={loading}
      errors={errors}
      saving={saving}
      learner={data?.learner}
      disabled={!isValid || !dirty || loading || submitting}
      onFirstNameChange={handleFirstNameChange}
      onLastNameChange={handleLastNameChange}
      onContactMobileChange={handleContactMobileChange}
      onContactEmailChange={handleContactEmailChange}
      onAddressLine1Change={handleAddressLine1Change}
      onAddressLine2Change={handleAddressLine2Change}
      onAddressTownChange={handleAddressTownChange}
      onAddressPostcodeChange={handleAddressPostcodeChange}
      onDateOfBirthChange={handleDateOfBirthChange}
      onSaveClicked={onSaveClicked}
      onCancelClicked={onCancelClicked}
      onTitleChange={handleTitleChange}
      open={isOpen}
      values={values}
    />
  );
};

export default LearnerDetailsModalStateProvider;
